import { useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { trpc } from "@/lib/trpc";
import { AJUDA_ORGANIZACAO_E_CONTA } from "@/lib/ajuda/dialogos/organizacao-e-conta";
import { useSwitchOrganization } from "@/lib/use-switch-organization";
import { recarregarNaOrganizacao } from "@/lib/recarregar-na-organizacao";
import { CreateOrganizationForm } from "@/components/organization/CreateOrganizationForm";
import { FaixaDeAtencao } from "@/components/organization/painel-geral/FaixaDeAtencao";
import { TabelaDasOrganizacoes } from "@/components/organization/painel-geral/TabelaDasOrganizacoes";
import { ListaNoCelular } from "@/components/organization/painel-geral/ListaNoCelular";
import { agrupar, atencaoDe, type Atencao, type OrgDoPainel } from "@/components/organization/painel-geral/painel";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { ORGANIZACOES_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";

const ZERO: Record<Atencao, number> = { atrasados: 0, aceite: 0, hoje: 0, vencido: 0, naoLidas: 0 };

/** Painel de gestão das organizações: o que pede atenção em todas, uma tabela por tipo com os
 *  números do papel da pessoa em cada uma; a linha expande o mês e os painéis pra agir nela. */
export function Organizations() {
  const isMobile = useIsMobile();
  const [criando, setCriando] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Atencao | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const [trocando, setTrocando] = useState<string | null>(null);

  useTourDaPagina(ORGANIZACOES_TOUR_STEPS);
  useSetPageHeader({ title: "Organizações", subtitle: "Acompanhe, troque e crie organizações" }, []);

  const ativaId = trpc.me.current.useQuery().data?.activeOrganization?.id;
  const resumo = trpc.painelDasOrganizacoes.resumo.useQuery();
  const naoLidas = trpc.notification.unreadCountByOrg.useQuery().data ?? {};
  const { switchTo } = useSwitchOrganization();

  const trocar = (o: OrgDoPainel) => {
    setTrocando(o.id);
    switchTo(o.id)
      .then(() => recarregarNaOrganizacao({ isPersonal: o.pessoal, types: o.tipos }))
      .catch((err) => {
        setTrocando(null);
        toast.error(err.message || "Erro ao trocar de organização.");
      });
  };

  // Uma aberta por vez: clicar de novo fecha.
  const alternar = (o: OrgDoPainel) => setAberta((atual) => (atual === o.id ? null : o.id));
  // Recolher pelo pé: a linha pode ter ficado lá em cima, então ela volta pra vista.
  const recolher = (o: OrgDoPainel) => {
    setAberta(null);
    requestAnimationFrame(() => document.getElementById(`org-${o.id}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
  };

  const orgs = resumo.data ?? [];
  const totais = orgs.reduce((t, o) => {
    const a = atencaoDe(o, naoLidas[o.id] ?? 0);
    return Object.fromEntries(Object.entries(t).map(([k, v]) => [k, v + a[k as Atencao]])) as Record<Atencao, number>;
  }, ZERO);
  const termo = busca.trim().toLowerCase();
  const visiveis = orgs.filter((o) => (!termo || o.nome.toLowerCase().includes(termo)) && (!filtro || atencaoDe(o, naoLidas[o.id] ?? 0)[filtro] > 0));
  const grupos = agrupar(visiveis, ativaId, naoLidas);

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-5 pb-10">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 sm:max-w-sm dark:border-border dark:bg-card">
          <Search className="h-4 w-4 shrink-0 text-zinc-400" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar organização" className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-400" />
        </label>
        <button
          type="button"
          data-tour="organizacoes-criar"
          onClick={() => setCriando(true)}
          className="ml-auto inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md bg-brand-600 px-3.5 text-[13px] font-semibold text-white shadow-sm hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          Criar organização
        </button>
      </div>

      {resumo.isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      ) : (
        <>
          <FaixaDeAtencao totais={totais} filtro={filtro} onFiltrar={setFiltro} />
          {grupos.length === 0 ? (
            <p className="py-12 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhuma organização com esse filtro.</p>
          ) : isMobile ? (
            <ListaNoCelular grupos={grupos} ativaId={ativaId} naoLidas={naoLidas} aberta={aberta} trocando={trocando} onAlternar={alternar} onTrocar={trocar} onRecolher={recolher} />
          ) : (
            <div className="space-y-4">
              {grupos.map((g) => (
                <TabelaDasOrganizacoes key={g.chave} grupo={g} ativaId={ativaId} naoLidas={naoLidas} trocando={trocando} aberta={aberta} onTrocar={trocar} onAlternar={alternar} onRecolher={recolher} />
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={criando} onOpenChange={setCriando}>
        <DialogContent className="max-w-2xl dark:border-border dark:bg-card">
          <DialogHeader>
            <DialogTitle className="dark:text-foreground" ajuda={AJUDA_ORGANIZACAO_E_CONTA.criarOrganizacao}>
              Criar organização
            </DialogTitle>
            <DialogDescription className="dark:text-muted-foreground">Organizações de equipe podem convidar outros membros.</DialogDescription>
          </DialogHeader>
          <CreateOrganizationForm onSuccess={() => setCriando(false)} onCancel={() => setCriando(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
